import { useCallback } from 'react';
import { useModals } from '@customerio/pluma-components/react';
import { useAnalytics } from '@/services/analytics';
import { SendEventModal, isResend, type SendEventModalData } from './components/send-event-modal';

/**
 * How long the backend takes to make a sent event readable.
 *
 * The delay lives here rather than in the modal because the callback belongs
 * to the caller: the modal closes and unmounts inside this window, so a timer
 * it owned would be cancelled before it ever fired.
 */
const RECORDED_AFTER_MS = 1000;

/**
 * Open the send-event modal.
 *
 * The "started" event is recorded here rather than inside the modal: opening
 * it is the thing being counted, and a caller that has to remember to record
 * it eventually forgets on one of the three surfaces that offer this.
 *
 * @public
 */
export function useOpenSendEventModal(): (data: SendEventModalData) => void {
  const modals = useModals();
  const analytics = useAnalytics();

  return useCallback(
    (data: SendEventModalData) => {
      void analytics.track('events', 'sendStarted', {
        Source: data.source,
        'Is Resend': isResend(data) ? 'Yes' : 'No',
      });
      const onSent = data.onSent;
      modals.open(
        SendEventModal,
        { ...data, onSent: onSent == null ? undefined : () => setTimeout(onSent, RECORDED_AFTER_MS) },
        { shouldCloseOnUnmount: true },
      );
    },
    [analytics, modals],
  );
}
